import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import {
  listAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
} from "@/lib/appointments.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarPlus, CalendarDays, Download, Trash2, Check, X, Plus } from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/appointments")({
  // Allow ?name=&email= so the CRM can deep-link "Schedule appointment" with the
  // customer prefilled.
  validateSearch: (s: Record<string, unknown>): { name?: string; email?: string } => ({
    name: typeof s.name === "string" ? s.name : undefined,
    email: typeof s.email === "string" ? s.email : undefined,
  }),
  head: () =>
    buildSeo({
      path: "/appointments",
      title: "Appointments — Reality Twin",
      description: "Schedule and track meetings with leads and customers, with calendar links.",
      noindex: true,
    }),
  component: AppointmentsPage,
});

function toGCalDate(iso: string): string {
  return new Date(iso)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

function googleCalUrl(a: any): string {
  const start = toGCalDate(a.starts_at);
  const end = toGCalDate(
    new Date(new Date(a.starts_at).getTime() + (a.duration_min ?? 30) * 60000).toISOString(),
  );
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: a.title,
    dates: `${start}/${end}`,
    details: a.notes ?? "",
    location: a.location ?? "",
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function downloadIcs(a: any) {
  const start = toGCalDate(a.starts_at);
  const end = toGCalDate(
    new Date(new Date(a.starts_at).getTime() + (a.duration_min ?? 30) * 60000).toISOString(),
  );
  const esc = (s: string) => (s ?? "").replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Reality Twin//Appointments//EN",
    "BEGIN:VEVENT",
    `UID:${a.id}@realitytwin.io`,
    `DTSTAMP:${toGCalDate(new Date().toISOString())}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${esc(a.title)}`,
    a.location ? `LOCATION:${esc(a.location)}` : "",
    a.notes ? `DESCRIPTION:${esc(a.notes)}` : "",
    a.contact_email
      ? `ATTENDEE;CN=${esc(a.contact_name ?? a.contact_email)}:mailto:${a.contact_email}`
      : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${a.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  link.click();
  URL.revokeObjectURL(url);
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const inp =
  "w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]";

function AppointmentsPage() {
  const t = useHydratedTranslation();
  const fetchList = useServerFn(listAppointments);
  const create = useServerFn(createAppointment);
  const update = useServerFn(updateAppointment);
  const remove = useServerFn(deleteAppointment);

  const [hasSession, setHasSession] = useState(false);
  useEffect(() => {
    let m = true;
    supabase.auth.getSession().then(({ data }) => m && setHasSession(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setHasSession(!!s));
    return () => {
      m = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const [list, setList] = useState<any[] | null>(null);
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "",
    contactName: "",
    contactEmail: "",
    startsAt: "",
    durationMin: 30,
    location: "",
    notes: "",
  });

  const prefill = Route.useSearch();
  useEffect(() => {
    if (prefill.name || prefill.email) {
      setComposing(true);
      setForm((f) => ({
        ...f,
        contactName: prefill.name ?? f.contactName,
        contactEmail: prefill.email ?? f.contactEmail,
      }));
    }
    // Run once on mount from the deep-link params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => fetchList().then((r) => setList(r.appointments));
  useEffect(() => {
    if (hasSession) load();
  }, [hasSession]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.startsAt || busy) return;
    setBusy(true);
    try {
      await create({
        data: {
          title: form.title,
          contactName: form.contactName || undefined,
          contactEmail: form.contactEmail || undefined,
          startsAt: form.startsAt,
          durationMin: Number(form.durationMin) || 30,
          location: form.location || undefined,
          notes: form.notes || undefined,
        },
      });
      toast.success(t("app.pages.appointments.scheduled"));
      setComposing(false);
      setForm({
        title: "",
        contactName: "",
        contactEmail: "",
        startsAt: "",
        durationMin: 30,
        location: "",
        notes: "",
      });
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.appointments.couldNotSchedule"));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(id: string, status: "scheduled" | "completed" | "cancelled") {
    try {
      await update({ data: { id, status } });
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.appointments.failed"));
    }
  }

  async function del(id: string) {
    if (!window.confirm(t("app.pages.appointments.deleteConfirm"))) return;
    try {
      await remove({ data: { id } });
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.appointments.failed"));
    }
  }

  const now = Date.now();
  const upcoming = (list ?? []).filter(
    (a) => a.status !== "cancelled" && new Date(a.starts_at).getTime() >= now,
  );
  const past = (list ?? []).filter(
    (a) => a.status === "cancelled" || new Date(a.starts_at).getTime() < now,
  );

  return (
    <DashboardShell
      title={t("app.pages.appointments.title")}
      subtitle={t("app.pages.appointments.subtitle")}
      actions={
        !composing ? (
          <button
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-1.5 rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium"
          >
            <Plus className="h-4 w-4" /> {t("app.pages.appointments.newAppointment")}
          </button>
        ) : undefined
      }
    >
      {composing && (
        <form onSubmit={submit} className="glass-strong rounded-2xl p-6 max-w-2xl space-y-3 mb-5">
          <div className="font-display text-lg font-semibold flex items-center gap-2">
            <CalendarPlus className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />{" "}
            {t("app.pages.appointments.newAppointment")}
          </div>
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t("app.pages.appointments.titlePh")}
            className={inp}
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              value={form.contactName}
              onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              placeholder={t("app.pages.appointments.contactNamePh")}
              className={inp}
            />
            <input
              type="email"
              value={form.contactEmail}
              onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              placeholder={t("app.pages.appointments.contactEmailPh")}
              className={inp}
            />
            <label className="block">
              <span className="text-xs text-muted-foreground">
                {t("app.pages.appointments.dateTime")}
              </span>
              <input
                required
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                className={`${inp} mt-1`}
              />
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground">
                {t("app.pages.appointments.duration")}
              </span>
              <input
                type="number"
                min={5}
                max={600}
                value={form.durationMin}
                onChange={(e) => setForm({ ...form, durationMin: Number(e.target.value) })}
                className={`${inp} mt-1`}
              />
            </label>
          </div>
          <input
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder={t("app.pages.appointments.locationPh")}
            className={inp}
          />
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder={t("app.pages.appointments.notesPh")}
            rows={2}
            className={`${inp} resize-none`}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium disabled:opacity-50"
            >
              {busy ? t("app.pages.appointments.scheduling") : t("app.pages.appointments.schedule")}
            </button>
            <button
              type="button"
              onClick={() => setComposing(false)}
              className="rounded-full border border-border px-5 py-2 text-sm hover:bg-white/5"
            >
              {t("app.pages.common.cancel")}
            </button>
          </div>
        </form>
      )}

      {list === null ? (
        <div className="space-y-2 max-w-2xl">
          {[0, 1].map((n) => (
            <div key={n} className="h-20 rounded-xl glass-strong animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 && !composing ? (
        <div className="glass-strong rounded-2xl p-10 text-center max-w-2xl">
          <CalendarDays className="h-8 w-8 mx-auto text-muted-foreground" />
          <div className="mt-3 font-medium">{t("app.pages.appointments.noAppointments")}</div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("app.pages.appointments.noAppointmentsSub")}
          </p>
          <button
            onClick={() => setComposing(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium"
          >
            <Plus className="h-4 w-4" /> {t("app.pages.appointments.newAppointment")}
          </button>
        </div>
      ) : (
        <div className="max-w-2xl space-y-6">
          {upcoming.length > 0 && (
            <Section title={t("app.pages.appointments.upcoming")}>
              {upcoming.map((a) => (
                <Card key={a.id} a={a} onStatus={setStatus} onDelete={del} />
              ))}
            </Section>
          )}
          {past.length > 0 && (
            <Section title={t("app.pages.appointments.pastCancelled")}>
              {past.map((a) => (
                <Card key={a.id} a={a} onStatus={setStatus} onDelete={del} muted />
              ))}
            </Section>
          )}
        </div>
      )}
    </DashboardShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-2">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Card({
  a,
  onStatus,
  onDelete,
  muted,
}: {
  a: any;
  onStatus: (id: string, s: "scheduled" | "completed" | "cancelled") => void;
  onDelete: (id: string) => void;
  muted?: boolean;
}) {
  const t = useHydratedTranslation();
  return (
    <div className={`glass-strong rounded-xl p-4 ${muted ? "opacity-70" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium flex items-center gap-2">
            {a.title}
            {a.status === "completed" && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30">
                {t("app.pages.appointments.statusCompleted")}
              </span>
            )}
            {a.status === "cancelled" && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 border border-red-500/30">
                {t("app.pages.appointments.statusCancelled")}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {fmt(a.starts_at)} · {a.duration_min}m{a.contact_name ? ` · ${a.contact_name}` : ""}
            {a.location ? ` · ${a.location}` : ""}
          </div>
          {a.notes && <div className="text-xs text-muted-foreground mt-1.5">{a.notes}</div>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <a
          href={googleCalUrl(a)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
        >
          <CalendarPlus className="h-3 w-3" /> {t("app.pages.appointments.googleCalendar")}
        </a>
        <button
          onClick={() => downloadIcs(a)}
          className="inline-flex items-center gap-1.5 rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
        >
          <Download className="h-3 w-3" /> .ics
        </button>
        {a.status !== "completed" && (
          <button
            onClick={() => onStatus(a.id, "completed")}
            className="inline-flex items-center gap-1.5 rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
          >
            <Check className="h-3 w-3" /> {t("app.pages.appointments.done")}
          </button>
        )}
        {a.status !== "cancelled" && (
          <button
            onClick={() => onStatus(a.id, "cancelled")}
            className="inline-flex items-center gap-1.5 rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
          >
            <X className="h-3 w-3" /> {t("app.pages.appointments.cancelAppt")}
          </button>
        )}
        <button
          onClick={() => onDelete(a.id)}
          className="ml-auto text-muted-foreground hover:text-red-400 p-1"
          title={t("app.pages.appointments.deleteTitle")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
